import { Component, EventEmitter, Output, OnInit, OnDestroy, inject } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { NavOverflowData, NavOverflowItem } from '@vg-constellation/angular-19/nav-overflow';
import {
  DELETE_VIEW_MESSAGE_ACTION,
  PREVIEW_VIEW_MESSAGE__ACTION,
  PRINT_VIEW_MESSAGE_ACTION,
} from '../../../core/constants/statement-messaging.constant';
import { AuthorizationService, UserPermissions } from '../../../services/read-write-role/authorization.service';

@Component({
  selector: 'app-view-update-message-navigation-overflow-menu',
  standalone: false,
  templateUrl: './view-update-message-navigation-overflow-menu.component.html',
  styleUrl: './view-update-message-navigation-overflow-menu.component.scss',
})
export class ViewUpdateMessageNavigationOverflowMenuComponent implements OnInit, OnDestroy {
  // ============================================
  // Services
  // ============================================
  private readonly authService = inject(AuthorizationService);
  private readonly destroy$ = new Subject<void>();

  // ============================================
  // Outputs
  // ============================================
  @Output() menuItemClicked = new EventEmitter<NavOverflowItem>();

  // ============================================
  // State
  // ============================================
  navigationMenu: NavOverflowData = { groups: [{ items: [] }] };
  private permissions: UserPermissions = {
    canView: false,
    canEdit: false,
    canCreate: false,
    canDelete: false,
    isReadOnly: true,
  };

  // ============================================
  // Lifecycle Hooks
  // ============================================

  ngOnInit(): void {
    this.subscribeToPermissions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================
  // Subscriptions
  // ============================================

  /**
   * Subscribe to permission changes and update menu
   */
  private subscribeToPermissions(): void {
    this.authService.permissions$
      .pipe(takeUntil(this.destroy$))
      .subscribe((permissions) => {
        this.permissions = permissions;
        this.updateMenuItems();
      });
  }

  // ============================================
  // Menu Building
  // ============================================

  /**
   * Update menu items based on permissions
   */
  private updateMenuItems(): void {
    const items: NavOverflowItem[] = [];

    // ✅ Preview and Print available to all users who can view
    if (this.permissions.canView) {
      items.push(PREVIEW_VIEW_MESSAGE__ACTION);
      items.push(PRINT_VIEW_MESSAGE_ACTION);
    }

    // ✅ Delete only available to users with delete permission
    if (this.permissions.canDelete) {
      items.push(DELETE_VIEW_MESSAGE_ACTION);
    }

    this.navigationMenu = {
      groups: [{ items }],
    };

    console.log('📋 View/Update menu items updated:', {
      total: items.length,
      canDelete: this.permissions.canDelete,
      items: items.map((item) => item.eventId),
    });
  }

  // ============================================
  // Event Handlers
  // ============================================

  /**
   * Handle menu item selection
   */
  onMessageNavigationMenu(item: NavOverflowItem): void {
    this.menuItemClicked.emit(item);
  }
}
